import { Injectable } from '@angular/core'
import { ViewerService } from './viewer.service'
import { combineLatest, map, Observable, of, shareReplay, switchMap, take } from 'rxjs'
import { GenericLayer } from '../../shared/model/layers/generic-layer'
import {
    isAnnotationLayer,
    isImageLayer,
    isSegmentationLayer,
} from '../../shared/model/layers/layer-types'
import { ImageLayer } from '../../shared/model/layers/image-layer'
import {
    NeuroglancerAnnotationUserLayer,
    NeuroglancerImageUserLayer,
    NeuroglancerSegmentationUserLayer,
} from '../../shared/model/neuroglancer-type-mappings'
import { SegmentationLayer } from '../../shared/model/layers/segmentation-layer'
import { environment } from '../../../environments/environment'
import { AnnotationLayer } from '../../shared/model/layers/annotation-layer'
import { TrackableService } from './trackable.service'
import { Store } from '@ngrx/store'
import {
    selectLayerInfos,
    selectLayerGroups,
    selectLayerInfosForGroupId,
} from '../../state/viewer/viewer.selectors'
import { LayerGroup } from '../../state/viewer/viewer.state'

@Injectable({
    providedIn: 'root',
})
export class ViewerLayersService {
    constructor(
        private readonly viewer: ViewerService,
        private readonly trackableService: TrackableService,
        private readonly store: Store
    ) {}

    private readonly imageLayers$: Observable<GenericLayer[]> = this.viewer.layers$.pipe(
        map(layers =>
            layers
                .filter(layer => isImageLayer(layer))
                .map(
                    layer =>
                        new ImageLayer(
                            layer.layer as NeuroglancerImageUserLayer,
                            this.store,
                            this.trackableService
                        )
                )
        )
    )

    private readonly segmentationLayers$: Observable<GenericLayer[]> = this.viewer.layers$.pipe(
        map(layers =>
            layers
                .filter(layer => isSegmentationLayer(layer))
                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                .map(
                    layer =>
                        new SegmentationLayer(
                            layer.layer as NeuroglancerSegmentationUserLayer,
                            this.store,
                            this.trackableService
                        )
                )
        )
    )

    private readonly annotationLayers$: Observable<GenericLayer[]> = this.viewer.layers$.pipe(
        map(layers =>
            layers.filter(
                layer =>
                    isAnnotationLayer(layer) &&
                    layer.name !== environment.selectionAnnotationLayerName
            )
        ),
        switchMap(layers => {
            return layers.length > 0
                ? combineLatest(
                      layers
                          .filter(layer => !!layer.layer)
                          .map(layer =>
                              layer.layer!.isReady
                                  ? of(layer)
                                  : this.trackableService
                                        .observeEvent(layer.layer!.readyStateChanged)
                                        .pipe(map(() => layer))
                          )
                  )
                : of([])
        }),
        map(layers =>
            layers.map(
                layer =>
                    new AnnotationLayer(
                        layer.layer as NeuroglancerAnnotationUserLayer,
                        this.store,
                        this.trackableService
                    )
            )
        )
    )

    private readonly layers$: Observable<GenericLayer[]> = combineLatest({
        image: this.imageLayers$,
        segmentation: this.segmentationLayers$,
        annotation: this.annotationLayers$,
    }).pipe(map(layers => [...layers.image, ...layers.segmentation, ...layers.annotation]))

    readonly layerGroups$: Observable<ViewerLayerNode[]> = combineLatest({
        groups: this.store.select(selectLayerGroups),
        layerInfos: this.store.select(selectLayerInfos),
        layers: this.layers$,
    }).pipe(
        map(layerGroupInfo => {
            const allLayersWithLayerId: ViewerLayerWithId[] = layerGroupInfo.layers.map(layer => ({
                layer,
                id:
                    layerGroupInfo.layerInfos.find(
                        l => l.name === layer.name && l.source === layer.source
                    )?.id ?? null,
                assignedToGroup: false,
            }))

            const layerGroups = [...layerGroupInfo.groups].map(group =>
                addViewerLayersToGroupRecursively(group, allLayersWithLayerId)
            )

            return layerGroups
        })
    )

    getLayersForGroup(groupId: string): Observable<GenericLayer[]> {
        return this.store.select(selectLayerInfosForGroupId(groupId)).pipe(
            switchMap(layerInfos =>
                this.layers$.pipe(
                    map(layers => {
                        return layerInfos
                            .map(info =>
                                layers.find(
                                    layer =>
                                        layer.name === info.name && layer.source === info.source
                                )
                            )
                            .filter((layer: GenericLayer): layer is GenericLayer => !!layer)
                    })
                )
            ),
            take(1),
            shareReplay()
        )
    }
}

const addViewerLayersToGroupRecursively = (
    layerGroup: LayerGroup,
    viewerLayers: ViewerLayerWithId[]
): ViewerLayerNode => {
    const layers = layerGroup.layerIds
        .map(l => {
            const layerWithId = viewerLayers.find(ll => ll.id === l)
            if (layerWithId) layerWithId.assignedToGroup = true
            return layerWithId
        })
        .map(l => l?.layer)
        .filter((l): l is GenericLayer => !!l)
        .map(l => ({ layer: l } as ViewerLayerNode))

    return {
        ...layerGroup,
        children: [
            ...layers,
            ...layerGroup.children.map(child =>
                addViewerLayersToGroupRecursively(child, viewerLayers)
            ),
        ],
    }
}

type ViewerLayerWithId = {
    layer: GenericLayer
    id?: string | null | undefined
    assignedToGroup: boolean
}

export type ViewerLayerNode = {
    id?: string
    name?: string
    layer?: GenericLayer | null
    children?: ViewerLayerNode[] | null
}